
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";

export interface EmailDraft {
  id: string;
  investorId: string;
  contactId: string;
  content: string;
  createdAt: string;
  label?: string;
}

/**
 * Save a draft email for a specific investor to the database
 */
export const saveEmailDraft = async (
  userId: string,
  investorId: string,
  contactId: string, 
  content: string,
  label?: string
): Promise<EmailDraft> => {
  if (!userId) {
    console.error("No user ID provided to saveEmailDraft");
    throw new Error("User ID is required");
  }
  
  const draftId = uuidv4();
  
  const newDraft: EmailDraft = {
    id: draftId,
    investorId,
    contactId,
    content,
    createdAt: new Date().toISOString(),
    label
  };
  
  try {
    // Save to Supabase email_templates table
    const { error } = await supabase
      .from('email_templates')
      .insert([{
        id: draftId,
        user_id: userId,
        investor_id: investorId,
        contact_id: contactId,
        content,
        label,
        created_at: new Date().toISOString()
      }]);
      
    if (error) {
      console.error("Error saving email draft to Supabase:", error);
      throw new Error(`Failed to save draft: ${error.message}`);
    }
    
    console.log(`Saved new email draft for investor ${investorId} to Supabase`);
    return newDraft;
  } catch (error) {
    console.error("Error in saveEmailDraft:", error);
    throw error;
  }
};

/**
 * Get all draft emails for a specific investor from the database
 */
export const getEmailDrafts = async (userId: string, investorId: string): Promise<EmailDraft[]> => {
  if (!userId) {
    console.error("No user ID provided to getEmailDrafts");
    return [];
  }
  
  try {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('user_id', userId)
      .eq('investor_id', investorId);
      
    if (error) {
      console.error("Error getting email drafts from Supabase:", error);
      return [];
    }
    
    if (!data || data.length === 0) {
      return [];
    }
    
    return data.map(draft => ({
      id: draft.id,
      investorId: draft.investor_id,
      contactId: draft.contact_id,
      content: draft.content,
      createdAt: draft.created_at,
      label: draft.label
    }));
  } catch (error) {
    console.error("Error in getEmailDrafts:", error);
    return [];
  }
};

/**
 * Delete a draft email from the database
 */
export const deleteEmailDraft = async (userId: string, draftId: string): Promise<boolean> => {
  if (!userId) {
    console.error("No user ID provided to deleteEmailDraft");
    return false;
  }
  
  try {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', draftId)
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error deleting email draft from Supabase:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in deleteEmailDraft:", error);
    return false;
  }
};

/**
 * Add a label to an email draft in the database
 */
export const labelEmailDraft = async (
  userId: string, 
  draftId: string, 
  label: string
): Promise<EmailDraft | null> => {
  if (!userId) {
    console.error("No user ID provided to labelEmailDraft");
    return null;
  }
  
  try {
    const { data, error } = await supabase
      .from('email_templates')
      .update({ label })
      .eq('id', draftId)
      .eq('user_id', userId)
      .select()
      .single();
      
    if (error) {
      console.error("Error updating email draft label in Supabase:", error);
      return null;
    }
    
    if (!data) {
      return null;
    }
    
    return {
      id: data.id,
      investorId: data.investor_id,
      contactId: data.contact_id,
      content: data.content,
      createdAt: data.created_at,
      label: data.label
    };
  } catch (error) {
    console.error("Error in labelEmailDraft:", error);
    return null;
  }
};
